import { FormDialog } from '@/components/FormDialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useGetBuilding, useGetBuildings } from '@/features/floormaps/hooks/useBuilding'
import { useMapEntityEditor } from '@/features/map/hooks/useMapEntityEditor'
import { INVALID_SHAPE_KEYS, InvalidShapeReason } from '@/features/map/types/validation'
import { useGetUsers } from '@/features/users/hooks/useUser'
import { isApiError } from '@/utils/error'
import { parseShapePoints } from '@/utils/shape'
import { showError, showSuccess } from '@/utils/toast'
import { LocateFixed } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapStage } from '../../map/components/MapState'
import { useMapDrawing } from '../../map/hooks/useMapDrawing'
import { Mode } from '../../map/types/mapStateProps'
import { Shape } from '../../map/types/shape'
import { useCreateDepartment, useGetDepartment, useUpdateDepartment } from '../hooks/useDepartment'

export function DepartmentDialog({
  open,
  onOpenChange,
  departmentId,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  departmentId?: string
}) {
  const { t } = useTranslation()
  const [selectedBuildingId, setSelectedBuildingId] = useState<string | null>(null)
  const { data: buildings = [], isLoading: isLoadingBuildings } = useGetBuildings(open)
  const { data: building, isFetching: isLoadingBuilding } = useGetBuilding(selectedBuildingId!)
  const { data: department, isLoading: isLoadingDepartment } = useGetDepartment(departmentId!, !!departmentId && open)
  const { data: users = [], isLoading: isLoadingUsers } = useGetUsers('user', open)

  const createDepartment = useCreateDepartment()
  const updateDepartment = useUpdateDepartment()

  const [mode, setMode] = useState<Mode>('view')
  const [isSaveShape, setIsSaveShape] = useState<boolean>(false)
  const [originalShape, setOriginalShape] = useState<Shape | null>(null)
  const [originalFloorId, setOriginalFloorId] = useState<string | null>(null)
  const [invalidShape, setInvalidShape] = useState<InvalidShapeReason | null>(null)
  const [isMapLoading, setIsMapLoading] = useState(false)

  const [form, setForm] = useState({ name: '', headId: '', floorId: '', keepPreviousHeadInDepartment: true })
  const isEdit = !!department
  const floors = building?.floors ?? []
  const selectedFloor = floors.find(f => f.id === form.floorId)
  const isLoading =
    isLoadingBuildings || isLoadingDepartment || createDepartment.isPending || updateDepartment.isPending
  const headChanged = isEdit && department && form.headId !== department.headId

  useEffect(() => {
    setInvalidShape(null)
    setIsSaveShape(false)
    resetPoints()
    setMode('view')

    if (department) {
      setSelectedBuildingId(department?.floor?.buildingId)

      setForm({
        name: department.name,
        headId: department.headId,
        floorId: department.floorId,
        keepPreviousHeadInDepartment: true,
      })

      if (department?.shape) {
        const shape: Shape = {
          id: department.id,
          label: department.name,
          color: '#22c55e',
          entityType: 'department',
          points: parseShapePoints(department.shape),
        }

        setShapes([shape])
        setOriginalShape(shape)
        setOriginalFloorId(department.floorId)
      } else {
        setShapes([])
      }
    } else {
      setSelectedBuildingId(null)
      setForm({ name: '', headId: '', floorId: '', keepPreviousHeadInDepartment: true })
      setShapes([])
    }
  }, [department, open])

  const { shapes, setShapes, hasShapeCollision, handleSaveShape, handleEditShape, handleCancelShape } =
    useMapEntityEditor({})

  const width = 600
  const height = 400
  const { points, handleClick, undo, resetPoints, finishShapeDrawing } = useMapDrawing(width, height, mode)

  const invalidMessage = invalidShape === INVALID_SHAPE_KEYS.MINIMUM_POINTS ? t('common.map.minimumPoints') : '\u00A0'

  const saveShape = () => {
    if (points.length < 6) {
      setInvalidShape(INVALID_SHAPE_KEYS.MINIMUM_POINTS)
      return
    }

    const result = finishShapeDrawing()
    if (!result) return

    handleSaveShape(result, 'department', form.name)
    resetPoints()
    setMode('view')
    setIsSaveShape(true)
  }

  const editShape = (id: string) => {
    handleEditShape(id)
    resetPoints()
    setMode('draw')
  }

  const cancelShape = () => {
    setInvalidShape(null)
    handleCancelShape()
    resetPoints()
    setMode('view')
  }

  const isInvalid = !form.name || !form.headId || !form.floorId || shapes.length <= 0

  function handleSubmit() {
    if (isInvalid) return

    const options = {
      onSuccess: () => {
        onOpenChange(false)
        showSuccess(isEdit ? t('department.toast.editSuccess') : t('department.toast.addSuccess'))
      },
      onError: (error: unknown) => {
        if (isApiError(error)) showError(error.message)
      },
    }

    const payload = {
      ...form,
      shape: shapes[0] ? JSON.stringify(shapes[0].points) : '',
    }

    if (isEdit && department) {
      updateDepartment.mutate({ id: department.id, payload: payload }, options)
    } else {
      createDepartment.mutate({ payload: payload }, options)
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? t('department.edit') : t('department.add')}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      isInvalid={isInvalid}
      dialogContentClass={'sm:max-w-2xl'}
      contentTestId="department-dialog"
      submitTestId="department-form-submit"
    >
      <div data-testid="department-form" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('department.name')} *</Label>
          <Input
            data-testid="department-form-name"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            className="h-9 bg-secondary/50 text-xs"
            placeholder=""
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('department.departmentHeader')} *</Label>
          <Select value={form.headId} onValueChange={v => setForm({ ...form, headId: v })} disabled={isLoadingUsers}>
            <SelectTrigger data-testid="department-form-head-trigger" className="h-9 bg-secondary/50 w-full">
              <SelectValue placeholder={t('department.placeholder.selectDepartmentHeader')} />
            </SelectTrigger>
            <SelectContent className="bg-card text-foreground">
              {users.map(b => (
                <SelectItem data-testid={`department-form-head-option-${b.id}`} key={b.id} value={b.id}>
                  {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('common.building')} *</Label>
          <Select
            value={selectedBuildingId ?? ''}
            disabled={isLoadingBuildings}
            onValueChange={v => {
              setSelectedBuildingId(v)
              setForm(prev => ({
                ...prev,
                floorId: '',
              }))
              setShapes([])
              resetPoints()
              setMode('view')
            }}
          >
            <SelectTrigger data-testid="department-form-building-trigger" className="h-9 bg-secondary/50 w-full">
              <SelectValue placeholder={t('common.placeholder.selectBuilding')} />
            </SelectTrigger>
            <SelectContent className="bg-card text-foreground">
              {buildings.map(b => (
                <SelectItem data-testid={`department-form-building-option-${b.id}`} key={b.id} value={b.id}>
                  {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">{t('common.floor')} *</Label>
          <Select
            value={form.floorId}
            disabled={isLoadingBuilding || !selectedBuildingId}
            onValueChange={v => {
              setForm({ ...form, floorId: v })

              if (v === originalFloorId && originalShape) {
                setShapes([originalShape])
              } else {
                setShapes([])
              }
              resetPoints()
              setMode('view')
            }}
          >
            <SelectTrigger data-testid="department-form-floor-trigger" className="h-9 bg-secondary/50 w-full">
              <SelectValue placeholder={t('common.placeholder.selectFloor')} />
            </SelectTrigger>
            <SelectContent className="bg-card text-foreground">
              {floors.map(f => (
                <SelectItem data-testid={`department-form-floor-option-${f.name}`} key={f.id} value={f.id}>
                  {f.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {headChanged && (
          <div className="flex flex-col gap-1.5 col-span-2">
            <div className="flex items-center gap-2">
              <Checkbox
                checked={form.keepPreviousHeadInDepartment}
                onCheckedChange={checked =>
                  setForm(prev => ({
                    ...prev,
                    keepPreviousHeadInDepartment: checked === true,
                  }))
                }
              />
              <Label>
                {t('department.keepPreviousHead')} ({department.head.name})
              </Label>
            </div>
            <span className="text-xs text-destructive">* {t('department.keepPreviousHeadWarning')}</span>
          </div>
        )}
      </div>

      <div>
        <div className="flex justify-end">
          <div className="flex flex-row gap-2">
            {mode === 'view' && (
              <>
                <Button
                  data-testid="department-map-reset"
                  variant={'outline'}
                  size="sm"
                  className="h-8 text-xs"
                  disabled={!form.floorId || !isSaveShape}
                  onClick={() => {
                    handleCancelShape()
                    setIsSaveShape(false)
                  }}
                >
                  {t('common.reset')}
                </Button>
                <Button
                  data-testid="department-map-add-location"
                  size="sm"
                  className="h-8 text-xs"
                  disabled={!form.floorId || isSaveShape || isMapLoading}
                  onClick={() => {
                    if (shapes.length > 0) {
                      editShape(shapes[0].id)
                    } else {
                      setMode('draw')
                    }
                  }}
                >
                  <LocateFixed className="size-3" />
                  {shapes.length > 0 ? t('common.editLocation') : t('common.addLocation')}
                </Button>
              </>
            )}

            {mode === 'draw' && (
              <>
                <Button data-testid="department-map-undo" size="sm" variant="outline" onClick={undo}>
                  {t('common.undo')}
                </Button>
                <Button
                  data-testid="department-map-cancel"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    cancelShape()
                  }}
                >
                  {t('common.cancel')}
                </Button>
                <Button data-testid="department-map-save" size="sm" onClick={saveShape}>
                  {t('common.save')}
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-col mt-4">
          <span className={`text-xs text-destructive text-end ${invalidShape ? 'visible' : 'invisible'}`}>
            {invalidMessage}
          </span>
          <MapStage
            imageId={selectedFloor?.imageId || ''}
            width={width}
            height={height}
            points={points}
            shapes={shapes}
            mode={mode}
            drawType={'shape'}
            onMapClick={handleClick}
            onMapLoadingChange={setIsMapLoading}
          />
        </div>
      </div>
    </FormDialog>
  )
}
